import { Highlightable } from '@angular/cdk/a11y';
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnInit,
   Output,
   ViewEncapsulation,
} from '@angular/core';

/** Event object emitted by LcsOptionComponent when selected or deselected. */
export class LcsOptionSelectionChange<T = any> {
   constructor(
      /** Reference to the option that emitted the event. */
      public source: LcsOptionComponent<T>,
      /** Whether the change in the option's value was a result of a user action. */
      public isUserInput = false
   ) {}
}

let nextUniqueId = 0;

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'lcs-option',
    templateUrl: './option.component.html',
    styleUrls: ['./option.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.lcs-option]': 'true',
        'role': 'option',
        '[class.lcs-active]': 'active',
        '[class.lcs-selected]': 'selected',
        '[attr.id]': 'id',
        '[attr.aria-selected]': 'selected ? true : null',
        '[attr.aria-disabled]': 'disabled.toString()',
        '[class.lcs-option-disabled]': 'disabled',
        '(click)': 'selectViaInteraction()',
    },
    standalone: false
})
export class LcsOptionComponent<TItem = any> implements Highlightable, OnInit {
   @Input() value: TItem | null;

   @Input()
   set id(id: string) {
      this._id = id || `lcs-option-${nextUniqueId++}`;
   }
   get id(): string {
      return this._id;
   }
   private _id: string;

   @Output() readonly selectionChange = new EventEmitter<LcsOptionSelectionChange>();

   active: boolean = false;

   disabled: boolean = false;

   selected: boolean = false;

   constructor(private changeDetectorRef: ChangeDetectorRef) {}

   ngOnInit(): void {
      if (!this.id) {
         this.id = this.id;
      }
   }

   select(): void {
      if (!this.selected) {
         this.selected = true;
         this.changeDetectorRef.markForCheck();
         this.emitSelectionChangeEvent();
      }
   }

   deselect(): void {
      if (this.selected) {
         this.selected = false;
         this.changeDetectorRef.markForCheck();
         this.emitSelectionChangeEvent();
      }
   }

   setActiveStyles(): void {
      if (!this.active) {
         this.active = true;
         this.changeDetectorRef.markForCheck();
      }
   }

   setInactiveStyles(): void {
      if (this.active) {
         this.active = false;
         this.changeDetectorRef.markForCheck();
      }
   }

   /**
    * `Selects the option while indicating the selection came from the user. Used to
    * determine if the select's view -> model callback should be invoked.`
    */
   selectViaInteraction(): void {
      if (!this.disabled) {
         this.selected = true;
         this.changeDetectorRef.markForCheck();
         this.emitSelectionChangeEvent(true);
      }
   }

   /** Emits the selection change event. */
   private emitSelectionChangeEvent(isUserInput = false): void {
      this.selectionChange.emit(new LcsOptionSelectionChange(this, isUserInput));
   }
}
